import { useState, useCallback, useEffect, useRef } from 'react';
import type { SalesDocType } from '@erp/data-access/interfaces';
import { useDataService } from '@/lib/service-context';

/**
 * All possible columns for sales document tables.
 * Each column has a key, a translation key for the header label,
 * and a flag indicating if it's always visible (non-toggleable).
 */
export interface ColumnDef {
  key: string;
  labelKey: string;
  /** Column cannot be hidden */
  locked?: boolean;
  /** Document types this column applies to. If omitted, applies to all. */
  appliesTo?: SalesDocType[];
}

export const ALL_SALES_COLUMNS: ColumnDef[] = [
  { key: 'number',           labelKey: 'sales.number',        locked: true },
  { key: 'client',           labelKey: 'sales.client' },
  { key: 'representative',   labelKey: 'representatives.representative' },
  { key: 'carRegistrationNumber', labelKey: 'sales.carRegistrationNumber' },
  { key: 'docDate',          labelKey: 'sales.docDate' },
  { key: 'validityDate',     labelKey: 'sales.validityDate' },
  { key: 'parentDoc',        labelKey: 'sales.parentDoc' },
  { key: 'channel',          labelKey: 'sales.channel' },
  { key: 'proCommandeCode',  labelKey: 'sales.proCommandeCode' },
  { key: 'totalHt',          labelKey: 'sales.totalHt' },
  { key: 'totalDiscount',    labelKey: 'sales.totalDiscount' },
  { key: 'totalVat',         labelKey: 'sales.totalVat' },
  { key: 'totalTtc',         labelKey: 'sales.totalTtc' },
  { key: 'withholdingAmount',labelKey: 'sales.withholding' },
  { key: 'stampAmount',      labelKey: 'sales.stampAmount' },
  { key: 'deliveryFee',      labelKey: 'sales.deliveryFee' },
  { key: 'netToPay',         labelKey: 'sales.netToPay' },
  { key: 'isInvoiced',       labelKey: 'sales.isInvoiced' },
  { key: 'paymentStatus',    labelKey: 'payments.statusLabel' },
  { key: 'benefit',          labelKey: 'sales.benefit' },
  { key: 'notes',            labelKey: 'sales.notes' },
  { key: 'createdAt',        labelKey: 'settings.columns.createdAt' },
  { key: 'updatedAt',        labelKey: 'settings.columns.updatedAt' },
];

/** Get columns applicable to a specific document type */
export function getColumnsForDocType(docType: SalesDocType): ColumnDef[] {
  return ALL_SALES_COLUMNS.filter((col) => !col.appliesTo || col.appliesTo.includes(docType));
}

/** Default visible columns per document type */
const DEFAULT_COLUMNS: Record<SalesDocType, string[]> = {
  quote:       ['number', 'client', 'docDate', 'validityDate', 'totalHt', 'totalTtc'],
  order:       ['number', 'client', 'docDate', 'totalHt', 'totalTtc', 'isInvoiced'],
  delivery:    ['number', 'client', 'docDate', 'totalHt', 'totalTtc', 'isInvoiced', 'paymentStatus'],
  invoice:     ['number', 'client', 'docDate', 'totalHt', 'totalVat', 'totalTtc', 'netToPay'],
  credit_note: ['number', 'client', 'docDate', 'totalHt', 'totalTtc'],
};

const STORAGE_KEY = 'erp_table_columns';

type ColumnsConfig = Record<SalesDocType, string[]>;

function loadConfig(): ColumnsConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as ColumnsConfig;
  } catch { /* ignore */ }
  return { ...DEFAULT_COLUMNS };
}

function saveConfig(config: ColumnsConfig) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
}

/**
 * Hook to get/set visible columns for a specific document type.
 */
export function useTableColumns(docType: SalesDocType) {
  const ds = useDataService();
  const loaded = useRef(false);
  const [config, setConfig] = useState<ColumnsConfig>(loadConfig);

  // Load from backend on mount
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    ds.userPreferences.get(STORAGE_KEY).then((val) => {
      if (val && typeof val === 'object') {
        const merged = { ...DEFAULT_COLUMNS, ...(val as ColumnsConfig) };
        setConfig(merged);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
      }
    }).catch(() => {});
  }, [ds]);

  const saveToBackend = useCallback((cfg: ColumnsConfig) => {
    saveConfig(cfg);
    ds.userPreferences.set(STORAGE_KEY, cfg).catch(() => {});
  }, [ds]);

  const visibleKeys = config[docType] ?? DEFAULT_COLUMNS[docType];

  const setVisibleColumns = useCallback((keys: string[]) => {
    setConfig((prev) => {
      const next = { ...prev, [docType]: keys };
      saveToBackend(next);
      return next;
    });
  }, [docType, saveToBackend]);

  const toggleColumn = useCallback((key: string) => {
    setConfig((prev) => {
      const current = prev[docType] ?? DEFAULT_COLUMNS[docType];
      const next = current.includes(key)
        ? current.filter((k) => k !== key)
        : [...current, key];
      const updated = { ...prev, [docType]: next };
      saveToBackend(updated);
      return updated;
    });
  }, [docType, saveToBackend]);

  const moveColumn = useCallback((from: number, to: number) => {
    setConfig((prev) => {
      const current = [...(prev[docType] ?? DEFAULT_COLUMNS[docType])];
      const [moved] = current.splice(from, 1);
      current.splice(to, 0, moved!);
      const updated = { ...prev, [docType]: current };
      saveToBackend(updated);
      return updated;
    });
  }, [docType, saveToBackend]);

  const resetToDefault = useCallback(() => {
    setConfig((prev) => {
      const next = { ...prev, [docType]: DEFAULT_COLUMNS[docType] };
      saveToBackend(next);
      return next;
    });
  }, [docType, saveToBackend]);

  return {
    visibleKeys,
    setVisibleColumns,
    toggleColumn,
    moveColumn,
    resetToDefault,
    allColumns: ALL_SALES_COLUMNS,
    applicableColumns: getColumnsForDocType(docType),
    defaults: DEFAULT_COLUMNS[docType],
  };
}

/**
 * Standalone function to load all configs (for settings page).
 */
export function loadAllColumnsConfig(): ColumnsConfig {
  return loadConfig();
}

export function saveAllColumnsConfig(config: ColumnsConfig) {
  saveConfig(config);
}

export function getDefaultColumns(): Record<SalesDocType, string[]> {
  return { ...DEFAULT_COLUMNS };
}
